import {ensureDeviceId} from "../features/auth/client-auth";
import type {DeviceId} from "../models/create-id";
import createId, {IdType} from "../models/create-id";
import type {AssetEntry, AssetId, GroupId, GroupRepo} from "../models/GroupRepo";
import type {UserRepo} from "../models/UserRepo";
import type {LocalAssetEntry} from "./local-asset-store";
import {
  addAssetToLocalStore,
  deleteAssetFromLocalStore,
  getAllAssetsFromLocalStore,
  getAssetFromLocalStore,
  withAssetUploadLock,
} from "./local-asset-store";
import {request} from "./request";
import {httpServerHost} from "./server-hosts";
import uploadFile, {uploadFileViaFetch} from "./upload-file";

interface SyncManager {
  getTags(): Promise<string[]>;
  register(tag: string): Promise<void>;
}

declare global {
  interface ServiceWorkerRegistration {
    readonly sync?: SyncManager;
  }

  interface SyncEvent extends ExtendableEvent {
    readonly lastChance: boolean;
    readonly tag: string;
  }

  interface ServiceWorkerGlobalScopeEventMap {
    sync: SyncEvent;
  }
}

type UploadFileOpts = {
  file: File;
  meta: AssetEntry["meta"];
  groupId: GroupId;
  groupRepo: GroupRepo;
  userRepo: UserRepo;
  withAssetId: (id: AssetId) => void;
};

const uploadLocalFirst = async (opts: UploadFileOpts & {deviceId: DeviceId | null}) => {
  const {file, meta, groupId, groupRepo, withAssetId, deviceId} = opts;
  const assetId = createId(IdType.Asset);
  await addAssetToLocalStore({
    id: assetId,
    groupId,
    fileName: file.name,
    mimeType: file.type,
    content: await file.arrayBuffer(),
    uploadResponse: null,
  });
  groupRepo.store.transaction(() => {
    groupRepo.Assets.set(assetId, {
      id: assetId,
      location: "local",
      size: file.size,
      name: file.name,
      type: "image",
      meta,
      createdAt: new Date(),
      deviceId,
    });
    withAssetId(assetId);
  });
};

const canDoBackgroundSync = async (): Promise<null | ServiceWorkerRegistration> => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!navigator.serviceWorker) return null;
  if (import.meta.env.DEV) return null;
  const registration = await navigator.serviceWorker.ready;
  if (registration.sync && "register" in registration.sync) {
    return registration;
  } else {
    return null;
  }
};

export const uploadFileLocalOrRemote = async (opts: UploadFileOpts) => {
  const deviceId = await ensureDeviceId(opts.userRepo);
  if (!deviceId) {
    await uploadLocalFirst({...opts, deviceId: null});
    return;
  }
  const registration = await canDoBackgroundSync();
  if (registration) {
    await uploadLocalFirst({...opts, deviceId});
    await registration.sync!.register("liste4000:assetSync");
  } else {
    const {file, groupId, groupRepo, withAssetId} = opts;
    const assetId = createId(IdType.Asset);
    const url = `${httpServerHost}/upload/${groupId}/${assetId}`;
    const res = await uploadFile(file, url);
    groupRepo.store.transaction(() => {
      groupRepo.Assets.set(assetId, {
        id: assetId,
        location: "remote",
        size: res.size,
        name: res.filename,
        type: "image",
        meta: res.probe ?? {},
        createdAt: new Date(),
        deviceId: null,
      });
      withAssetId(assetId);
    });
  }
};

const processUploadedLocalAssets = async (opts: {
  assetId: AssetId;
  store: GroupRepo["store"];
  response: NonNullable<LocalAssetEntry["uploadResponse"]>;
}) => {
  const {assetId, store, response} = opts;
  store.setPartialRow("assets", assetId, {
    location: "remote",
    deviceId: undefined,
    size: response.size,
    name: response.filename,
    meta: JSON.stringify(response.probe ?? {}),
  });
  await deleteAssetFromLocalStore(assetId);
};

// called from browser once the service worker has uploaded
export const onAssetUploaded = async (assetId: AssetId, store: GroupRepo["store"]) => {
  const localAsset = await getAssetFromLocalStore(assetId);
  const response = localAsset?.uploadResponse;
  if (response) await processUploadedLocalAssets({assetId, store, response});
};

export const checkForUploadedAssets = async (store: GroupRepo["store"]) => {
  await uploadAllLocalAssets(store);
  const localAssets = await getAllAssetsFromLocalStore();
  for (const asset of localAssets) {
    if (!asset.uploadResponse) continue;
    await processUploadedLocalAssets({assetId: asset.id, store, response: asset.uploadResponse});
  }
};

const deleteRemoteAsset = async (asset: Pick<AssetEntry, "id" | "name">, groupId: GroupId) => {
  await request("DELETE", `/upload/${groupId}/${asset.id}`, {fileName: asset.name});
};

export const onRemoveAsset = async (
  asset: Pick<AssetEntry, "id" | "location" | "name">,
  groupId: GroupId
) => {
  if (asset.location === "local") {
    const localAsset = await getAssetFromLocalStore(asset.id);
    if (localAsset) {
      await deleteAssetFromLocalStore(asset.id);
      if (localAsset.uploadResponse) {
        await deleteRemoteAsset(asset, groupId);
      }
    }
  } else {
    await deleteRemoteAsset(asset, groupId);
  }
};

const uploadAllLocalAssets = async (store: GroupRepo["store"]) =>
  withAssetUploadLock(async () => {
    const localAssets = await getAllAssetsFromLocalStore();
    const groupAssets = store.getTable("assets");
    for (const asset of localAssets) {
      if (asset.uploadResponse) continue;
      const dbAsset = groupAssets[asset.id];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!dbAsset || !dbAsset.deviceId) continue;
      const url = `${httpServerHost}/upload/${asset.groupId}/${asset.id}`;
      const content: ArrayBuffer = asset.content;
      const file = new File([content], asset.fileName, {type: asset.mimeType});
      let response;
      try {
        response = await uploadFileViaFetch(file, url);
      } catch (e) {
        // keep going, so the remaining assets (and already uploaded ones) still get processed
        console.warn(`Failed to upload asset ${asset.id}`, e);
        continue;
      }
      store.setPartialRow("assets", asset.id, {
        location: "remote",
        deviceId: undefined,
        size: response.size,
        name: response.filename,
        meta: JSON.stringify(response.probe ?? {}),
      });
      await deleteAssetFromLocalStore(asset.id);
    }
  });

export const syncAllLocalAssetsWithNewDeviceId = async (
  deviceId: DeviceId,
  groupRepp: GroupRepo
) => {
  groupRepp.store.transaction(() => {
    const assets = Object.values(groupRepp.Assets.getAll());
    for (const asset of assets) {
      if (asset.location === "local") {
        groupRepp.Assets.patch(asset.id, {deviceId});
      }
    }
  });
  await uploadAllLocalAssets(groupRepp.store);
};
